import { apiHelpers } from '../utils/api';

export interface Customer {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  rentalContracts: {
    id: string;
    contractNumber: string;
    status: string;
    startDate: string;
    endDate?: string;
    _count: {
      machines: number;
    };
  }[];
}

export interface Machine {
  id: string;
  serialNumber: string;
  productName: string;
  status: string;
  rentStartDate?: string;
  rentEndDate?: string;
  returnDate?: string;
  contractNumber: string;
  contractStatus: string;
  totalRefills: number;
}

export interface RefillDetail {
  oilProductId: string;
  oilProductName: string;
  oilProductSku?: string;
  oilScentName: string;
  amountML: number;
  amountLiters: string;
  costPerML: number;
  totalCost: number;
  warehouseName: string;
  notes?: string;
}

export interface RefillVisit {
  id: string;
  visitNumber: string;
  visitDate: string;
  visitType: 'REFILL' | 'SERVICE' | 'SCENT_CHANGE' | 'SAMPLE_DELIVERY';
  visitStatus: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'NEEDS_FOLLOWUP';
  priority: 'NORMAL' | 'URGENT';
  employeeName: string;
  requestedByCustomer: boolean;
  requestNotes?: string;
  completionNotes?: string;
  issueResolved?: boolean;
  followupRequired: boolean;
  fromScent?: string;
  toScent?: string;
  details: RefillDetail[];
  totalOilUsedML: number;
  totalCost: number;
  notes?: string;
}

export interface OilScentUsage {
  productId: string;
  productName: string;
  sku: string;
  totalML: number;
  count: number;
}

export interface MachineRefillHistory {
  machine: {
    id: string;
    serialNumber: string;
    productName: string;
    status: string;
    rentStartDate?: string;
    rentEndDate?: string;
    returnDate?: string;
    monthlyRentalFee?: number;
  };
  customer: {
    id: string;
    name: string;
    phone?: string;
    email?: string;
    address?: string;
  };
  contract: {
    id: string;
    contractNumber: string;
    startDate: string;
    endDate?: string;
    status: string;
  };
  refillSchedule?: {
    scheduleType: string;
    intervalDays: number;
    nextRefillDate: string;
    lastRefillDate?: string;
  } | null;
  summary: {
    totalRefills: number;
    totalOilUsedML: number;
    totalOilUsedLiters: string;
    totalCost: number;
    oilScentsUsed: OilScentUsage[];
  };
  refillHistory: RefillVisit[];
}

export interface GetMachineRefillHistoryQuery {
  startDate?: string;
  endDate?: string;
}

export const machineReportService = {
  // Get all customers with rental contracts
  getCustomersWithContracts: async (): Promise<{ success: boolean; data: Customer[] }> => {
    return await apiHelpers.get('/machine-reports/customers');
  },

  // Get machines for a specific customer
  getCustomerMachines: async (customerId: string): Promise<{ success: boolean; data: Machine[] }> => {
    return await apiHelpers.get(`/machine-reports/customers/${customerId}/machines`);
  },

  // Get machine refill history
  getMachineRefillHistory: async (
    machineId: string,
    query?: GetMachineRefillHistoryQuery
  ): Promise<{ success: boolean; data: MachineRefillHistory }> => {
    const params = new URLSearchParams();
    if (query?.startDate) params.append('startDate', query.startDate);
    if (query?.endDate) params.append('endDate', query.endDate);

    const queryString = params.toString();
    const url = `/machine-reports/machines/${machineId}/refill-history${queryString ? `?${queryString}` : ''}`;

    return await apiHelpers.get(url);
  },

  // Generate and download PDF report for machine refill history
  downloadMachineReportPDF: async (
    machineId: string,
    query?: GetMachineRefillHistoryQuery
  ): Promise<Blob> => {
    const params = new URLSearchParams();
    if (query?.startDate) params.append('startDate', query.startDate);
    if (query?.endDate) params.append('endDate', query.endDate);

    const queryString = params.toString();
    const url = `/machine-reports/machines/${machineId}/refill-history/pdf${queryString ? `?${queryString}` : ''}`;

    const token = localStorage.getItem('token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}${url}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download PDF report');
    }

    return await response.blob();
  },
};
